export const RETIREMENT_BATCH_STATUS = Object.freeze({
  DRAFT: 'DRAFT',
  UNDER_REVIEW: 'UNDER_REVIEW',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
});

export const RETIREMENT_CRITICAL_FIELDS = Object.freeze([
  'quantityTco2e', 'registry', 'standard', 'projectReference', 'retirementReference',
  'serialRange', 'vintage', 'retirementDate', 'beneficiary', 'evidenceRecords',
]);

const numberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const text = value => String(value || '').trim();

export function normalizeRetirementBatch(batch = {}, index = 0) {
  return {
    ...batch,
    id: batch.id || `retirement-batch-${index + 1}`,
    quantityTco2e: numberOrNull(batch.quantityTco2e ?? batch.quantity),
    registry: text(batch.registry || batch.creditStandard),
    standard: text(batch.standard || batch.creditStandard),
    projectReference: text(batch.projectReference),
    retirementReference: text(batch.retirementReference || batch.registryReference),
    serialRange: text(batch.serialRange || batch.vintageOrSerial),
    vintage: text(batch.vintage),
    retirementDate: text(batch.retirementDate),
    beneficiary: text(batch.beneficiary || batch.ownershipBeneficiary),
    creditPriceJpyPerTco2e: numberOrNull(batch.creditPriceJpyPerTco2e),
    priceSource: text(batch.priceSource),
    verificationStatus: Object.values(RETIREMENT_BATCH_STATUS).includes(batch.verificationStatus)
      ? batch.verificationStatus
      : RETIREMENT_BATCH_STATUS.DRAFT,
    evidenceRecords: (batch.evidenceRecords || []).map(record => ({ ...record })),
    audit: (batch.audit || []).map(event => ({ ...event })),
  };
}

export function normalizeRetirementBatches(annualResult = {}, action = {}) {
  if ((annualResult.retirementBatches || []).length > 0) {
    return annualResult.retirementBatches.map(normalizeRetirementBatch);
  }
  const fieldValues = action.fieldValues || {};
  const quantity = numberOrNull(annualResult.retiredQuantity ?? fieldValues.retiredQuantity);
  if (quantity === null) return [];
  return [normalizeRetirementBatch({
    id: `${action.id || 'legacy-credit'}-${annualResult.fiscalYear || action.fiscalYear || 'fy'}-batch-1`,
    quantityTco2e: quantity,
    registry: annualResult.creditStandard || fieldValues.creditStandard,
    standard: annualResult.creditStandard || fieldValues.creditStandard,
    retirementReference: annualResult.registryReference || fieldValues.registryReference,
    serialRange: annualResult.vintageOrSerial || fieldValues.vintageOrSerial,
    vintage: annualResult.vintageOrSerial || fieldValues.vintageOrSerial,
    retirementDate: annualResult.retirementDate || fieldValues.retirementDate,
    beneficiary: annualResult.beneficiary || fieldValues.beneficiary || 'Carbon Insight internal account',
    verificationStatus: annualResult.verificationStatus === 'VERIFIED'
      ? RETIREMENT_BATCH_STATUS.VERIFIED
      : annualResult.verificationStatus === 'UNDER_REVIEW'
        ? RETIREMENT_BATCH_STATUS.UNDER_REVIEW
        : RETIREMENT_BATCH_STATUS.DRAFT,
    evidenceRecords: annualResult.evidenceRecords || [],
  })];
}

export function retirementBatchSummary(batches = []) {
  return batches.map(normalizeRetirementBatch).reduce((summary, batch) => {
    const quantity = batch.quantityTco2e || 0;
    summary.totalTco2e += quantity;
    if (batch.verificationStatus === RETIREMENT_BATCH_STATUS.VERIFIED) {
      summary.verifiedTco2e += quantity;
      summary.verifiedCount += 1;
    } else if (batch.verificationStatus === RETIREMENT_BATCH_STATUS.UNDER_REVIEW) {
      summary.underReviewTco2e += quantity;
      summary.underReviewCount += 1;
    } else if (batch.verificationStatus === RETIREMENT_BATCH_STATUS.REJECTED) {
      summary.rejectedTco2e += quantity;
      summary.rejectedCount += 1;
    }
    return summary;
  }, {
    totalTco2e: 0,
    verifiedTco2e: 0,
    underReviewTco2e: 0,
    rejectedTco2e: 0,
    verifiedCount: 0,
    underReviewCount: 0,
    rejectedCount: 0,
    batchCount: batches.length,
  });
}

const hasRetirementCertificate = batch => batch.evidenceRecords.some(record => (
  String(record.evidenceType || record.type || '').toUpperCase() === 'RETIREMENT_CERTIFICATE'
  && text(record.name || record.reference || record.url)
));

export function validateRetirementBatch(batch, fiscalYear, allBatches = []) {
  const normalized = normalizeRetirementBatch(batch);
  const errors = [];
  if (!(normalized.quantityTco2e > 0)) errors.push('QUANTITY_REQUIRED');
  if (!normalized.registry && !normalized.standard) errors.push('REGISTRY_REQUIRED');
  if (!normalized.retirementReference) errors.push('RETIREMENT_REFERENCE_REQUIRED');
  if (!normalized.serialRange) errors.push('SERIAL_REQUIRED');
  if (!normalized.vintage) errors.push('VINTAGE_REQUIRED');
  if (!normalized.beneficiary) errors.push('BENEFICIARY_REQUIRED');
  const retirementYear = Number(normalized.retirementDate.slice(0, 4));
  if (!normalized.retirementDate || retirementYear !== Number(fiscalYear)) {
    errors.push('RETIREMENT_DATE_OUTSIDE_FY');
  }
  if (!hasRetirementCertificate(normalized)) errors.push('CERTIFICATE_REQUIRED');
  const duplicateIdentity = allBatches.map(normalizeRetirementBatch).some(candidate => (
    candidate.id !== normalized.id
    && (
      (candidate.retirementReference
        && candidate.retirementReference === normalized.retirementReference)
      || (candidate.serialRange && candidate.serialRange === normalized.serialRange)
    )
  ));
  if (duplicateIdentity) errors.push('DUPLICATE_RETIREMENT_IDENTITY');
  return { valid: errors.length === 0, errors, batch: normalized };
}

const criticalSnapshot = batch => Object.fromEntries(
  RETIREMENT_CRITICAL_FIELDS.map(field => [field, batch[field] ?? null]),
);

export function updateRetirementBatch(existingBatch, batchDraft, audit = {}) {
  const existing = normalizeRetirementBatch(existingBatch);
  const next = normalizeRetirementBatch({ ...existing, ...batchDraft, id: existing.id });
  const criticalChanged = JSON.stringify(criticalSnapshot(existing))
    !== JSON.stringify(criticalSnapshot(next));
  if (existing.verificationStatus === RETIREMENT_BATCH_STATUS.VERIFIED && criticalChanged) {
    next.verificationStatus = RETIREMENT_BATCH_STATUS.UNDER_REVIEW;
    next.audit = [...existing.audit, {
      type: 'VERIFICATION_INVALIDATED',
      actor: audit.actor || 'Demo Admin',
      occurredAt: audit.occurredAt || null,
    }];
  }
  return next;
}

export function submitRetirementBatch(batch, fiscalYear, allBatches = [], audit = {}) {
  const validation = validateRetirementBatch(batch, fiscalYear, allBatches);
  if (!validation.valid) throw new RangeError(validation.errors.join(','));
  return {
    ...validation.batch,
    verificationStatus: RETIREMENT_BATCH_STATUS.UNDER_REVIEW,
    evidenceRecords: validation.batch.evidenceRecords.map(record => ({
      ...record, status: RETIREMENT_BATCH_STATUS.UNDER_REVIEW,
    })),
    audit: [...validation.batch.audit, {
      type: 'SUBMITTED_FOR_REVIEW', actor: audit.actor || 'Demo Admin',
      occurredAt: audit.occurredAt || null,
    }],
  };
}

export function verifyRetirementBatch(batch, fiscalYear, allBatches = [], audit = {}) {
  const validation = validateRetirementBatch(batch, fiscalYear, allBatches);
  if (!validation.valid) throw new RangeError(validation.errors.join(','));
  if (validation.batch.verificationStatus !== RETIREMENT_BATCH_STATUS.UNDER_REVIEW) {
    throw new RangeError('RETIREMENT_BATCH_NOT_UNDER_REVIEW');
  }
  return {
    ...validation.batch,
    verificationStatus: RETIREMENT_BATCH_STATUS.VERIFIED,
    evidenceRecords: validation.batch.evidenceRecords.map(record => ({
      ...record, status: RETIREMENT_BATCH_STATUS.VERIFIED,
    })),
    audit: [...validation.batch.audit, {
      type: 'VERIFIED_AND_INCLUDED_IN_BALANCE', actor: audit.actor || 'Demo Admin',
      occurredAt: audit.occurredAt || null,
    }],
  };
}

/** Reject one submitted retirement batch while preserving its identity and audit trail. */
export function rejectRetirementBatch(batch, reason, audit = {}) {
  const normalized = normalizeRetirementBatch(batch);
  const rejectionReason = text(reason);
  if (!rejectionReason) throw new RangeError('A rejection reason is required.');
  if (normalized.verificationStatus !== RETIREMENT_BATCH_STATUS.UNDER_REVIEW) {
    throw new RangeError('RETIREMENT_BATCH_NOT_UNDER_REVIEW');
  }
  return {
    ...normalized,
    verificationStatus: RETIREMENT_BATCH_STATUS.REJECTED,
    evidenceRecords: normalized.evidenceRecords.map(record => ({
      ...record, status: RETIREMENT_BATCH_STATUS.REJECTED,
    })),
    audit: [...normalized.audit, {
      type: 'REJECTED',
      actor: audit.actor || 'Demo Admin',
      occurredAt: audit.occurredAt || null,
      reason: rejectionReason,
    }],
  };
}
